/**
 * Report timestamps are normally localized by the API to the family's ZIP-code
 * timezone before they reach this viewer. JavaScript normally rebases an ISO
 * timestamp with a non-zero offset into the device timezone, which can move the
 * displayed time by several hours for a clinician viewing the report elsewhere.
 *
 * Build a UTC-backed Date from the timestamp's wall-clock fields so locale
 * formatting can be used without applying a second timezone conversion. When
 * ZIP-code timezone resolution is unavailable, the API falls back to a zero-
 * offset UTC timestamp. Convert that fallback to the viewer's local wall clock,
 * matching the sent/unsent record screens.
 */
export const parseReportWallClock = (value?: string | null): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/,
  );
  if (!match) return null;

  const isUtcFallback = /(?:Z|[+-]00:?00)$/i.test(trimmed);
  if (isUtcFallback) {
    const instant = new Date(trimmed);
    if (Number.isNaN(instant.getTime())) return null;

    return new Date(Date.UTC(
      instant.getFullYear(),
      instant.getMonth(),
      instant.getDate(),
      instant.getHours(),
      instant.getMinutes(),
      instant.getSeconds(),
      instant.getMilliseconds(),
    ));
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4] ?? 0);
  const minute = Number(match[5] ?? 0);
  const second = Number(match[6] ?? 0);
  const millisecond = Number((match[7] ?? '').slice(0, 3).padEnd(3, '0'));

  const parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, second, millisecond));
  const isValid =
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day &&
    parsed.getUTCHours() === hour &&
    parsed.getUTCMinutes() === minute &&
    parsed.getUTCSeconds() === second;

  return isValid ? parsed : null;
};

export const formatReportWallClock = (
  value: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions,
): string | null => {
  const parsed = parseReportWallClock(value);
  if (!parsed) return null;

  return parsed.toLocaleString(locale, {
    ...options,
    timeZone: 'UTC',
  });
};
